<script setup lang="ts">
import { computed } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";

import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import PrimaryButton from "@/views/blocks/shared/section/buttons/primary-button/PrimaryButton.vue";
import Image from "@/views/blocks/shared/section/image/Image.vue";

import type { SplitWithImageRightProps } from "./cta.interfaces";

const props = defineProps<{
    block: PageBlock<SplitWithImageRightProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :background-color="styleValues.background">
        <div class="flex flex-wrap">
            <!-- Content Column (Left) -->
            <div class="md:w-1/2 flex flex-col justify-center pb-16 pt-32 md:pb-32">
                <div class="max-w-2xl ml-auto px-6 lg:px-8 md:pr-0">
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        field-name="tag"
                    >
                        <p :style="{ color: styleValues.brand }" class="font-semibold mb-4">
                            {{ dataValues.tag.data.text }}
                        </p>
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :tag="dataValues.title.data.tag"
                            :text="dataValues.title.data.text"
                            :text-color="styleValues.text"
                            class="text-4xl sm:text-5xl"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :text-color="styleValues.text"
                            class="mt-6 text-lg/8 opacity-70"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.primaryButton.isHidden"
                        :blockId="block.id"
                        field-name="primaryButton"
                    >
                        <PrimaryButton
                            :label="dataValues.primaryButton.data.label"
                            :href="dataValues.primaryButton.data.href"
                            :text-color="styleValues.background"
                            :background-color="styleValues.brand"
                            class="px-3.5 py-2.5 mt-10 rounded-lg text-sm"
                        />
                    </EditableElement>
                </div>
            </div>

            <!-- Image Column (Right) - Full height -->
            <div class="md:ml-auto md:w-1/2 max-h-64 md:max-h-[760px] md:pl-16 lg:pl-32">
                <EditableElement
                    v-if="!dataValues.image.isHidden"
                    :blockId="block.id"
                    field-name="image"
                >
                    <Image
                        :src="dataValues.image.data.src"
                        :alt="dataValues.image.data.alt"
                        class="md:ml-auto w-full md:w-auto h-full object-cover"
                        width="760"
                        height="760"
                    />
                </EditableElement>
            </div>
        </div>
    </Section>
</template>
